import { SITE_PHOTOS } from '../sitePhotos';
import ConstructionPhotos from '../components/ConstructionPhotos';
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSiteInfo } from '../context/SiteContext';
import Sidebar from '../components/Sidebar';
import { publicCollection } from '../lib/publicContent';
import { cleanQuillHtml } from '../lib/imageCompression';

export default function About() {
  const { siteInfo } = useSiteInfo();
  const [pageContent, setPageContent] = useState<string>('');
  const heroImage = SITE_PHOTOS[0].url;

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const page = (await publicCollection('pages')).find(p=>p.id==='about');
        if(page?.content) setPageContent(cleanQuillHtml(page.content));
      } catch (error) {
        console.error('Error fetching about page content:', error);
      }
    };
    fetchPageContent();
  }, []);
  
  return (
    <div className="container mx-auto px-4 py-8 md:py-12 flex flex-col md:flex-row gap-8">
      <Helmet>
        <title>Giới thiệu - {siteInfo.name}</title>
        <meta name="description" content={`Giới thiệu về ${siteInfo.name}, đơn vị tiên phong trong lĩnh vực thi công nền móng, khoan cọc nhồi, ép cừ và xử lý nền móng yếu.`} />
        <link rel="canonical" href={`${siteInfo.baseUrl}/gioi-thieu`} />
        <meta property="og:title" content={`Giới thiệu - ${siteInfo.name}`} />
        <meta property="og:description" content={`Giới thiệu về ${siteInfo.name}, đơn vị tiên phong trong lĩnh vực thi công nền móng, khoan cọc nhồi, ép cừ và xử lý nền móng yếu.`} />
        <meta property="og:image" content={heroImage.startsWith("/") ? `${siteInfo.baseUrl}${heroImage}` : heroImage} />
        <meta property="og:url" content={`${siteInfo.baseUrl}/gioi-thieu`} />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="w-full md:w-3/4 min-w-0">
        <nav className="text-sm text-gray-500 mb-6 flex flex-wrap items-center gap-x-2 gap-y-1">
          <a href="/" className="hover:text-primary whitespace-nowrap">Trang chủ</a>
          <span>/</span>
          <span className="text-gray-800 font-medium break-words max-w-full">Về chúng tôi</span>
        </nav>

        <article className="bg-white p-6 md:p-8 rounded-lg shadow-sm border prose max-w-none break-words">
          <h1 className="text-2xl md:text-3xl font-bold text-primary mb-8 pb-2 border-b-2 border-primary inline-block">
            Giới thiệu công ty
          </h1>

          {pageContent ? (
            <>
              <div className="not-prose mb-8 rounded-lg overflow-hidden">
                <img src={heroImage} alt="Giới thiệu công ty" className="w-full h-auto rounded-xl" />
              </div>
              <div className="article-content ql-editor break-words" dangerouslySetInnerHTML={{ __html: cleanQuillHtml(pageContent) }} />
            </>
          ) : (
            <>
              <div className="not-prose mb-8 rounded-lg overflow-hidden border shadow-sm">
                <img 
                  src={heroImage} 
                  alt="Giới thiệu công ty" 
                  className="block w-full h-auto" 
                />
              </div>

              <p className="text-lg font-medium text-gray-700 mb-6">
                Chào mừng Quý khách đến với <strong>{siteInfo.name}</strong>! Chúng tôi là đơn vị tiên phong và uy tín hàng đầu trong lĩnh vực thi công nền móng, khoan cọc nhồi, ép cừ và xử lý nền móng yếu tại Việt Nam.
              </p>

              <h3 className="text-xl font-bold text-primary mt-8 mb-4">1. Tầm nhìn và Sứ mệnh</h3>
              <p>
                Với phương châm "Chất lượng tạo niềm tin - Uy tín làm nên thương hiệu", chúng tôi không ngừng nỗ lực cải tiến công nghệ, nâng cao trình độ chuyên môn của đội ngũ kỹ sư và công nhân để mang đến những giải pháp thi công tối ưu nhất, an toàn nhất và tiết kiệm nhất cho mọi công trình.
              </p>

              <h3 className="text-xl font-bold text-primary mt-8 mb-4">2. Lĩnh vực hoạt động chính</h3>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li><strong>Khoan cọc nhồi dân dụng và công nghiệp:</strong> Thi công cọc nhồi đường kính từ D300 đến D1500, phục vụ các công trình nhà phố, biệt thự, nhà xưởng, cầu đường.</li>
                <li><strong>Khoan cọc nhồi mini:</strong> Giải pháp hoàn hảo cho các công trình trong ngõ hẹp, khu vực đông dân cư.</li>
                <li><strong>Chống văng và ép cừ U200:</strong> Bảo vệ an toàn tuyệt đối cho các công trình lân cận khi đào móng sâu, thi công tầng hầm.</li>
                <li><strong>Chống lún nhà, xử lý nền móng yếu:</strong> Khắc phục triệt để tình trạng lún nứt, nghiêng nhà bằng các công nghệ hiện đại.</li>
              </ul>

              <h3 className="text-xl font-bold text-primary mt-8 mb-4">3. Hồ sơ năng lực</h3>
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mb-6">
                <ul className="space-y-3">
                  <li><strong>Tên công ty:</strong> {siteInfo.name}</li>
                  <li><strong>Mã số thuế:</strong> {siteInfo.taxCode}</li>
                  <li><strong>Người đại diện:</strong> {siteInfo.director}</li>
                  <li><strong>Địa chỉ trụ sở:</strong> {siteInfo.address}</li>
                  <li><strong>Điện thoại:</strong> {siteInfo.phone}</li>
                  <li><strong>Email:</strong> {siteInfo.email}</li>
                </ul>
              </div>

              <h3 className="text-xl font-bold text-primary mt-8 mb-4">4. Tại sao chọn chúng tôi?</h3>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li><strong>Kinh nghiệm dày dặn:</strong> Đội ngũ kỹ sư, chuyên gia hàng chục năm kinh nghiệm thực chiến.</li>
                <li><strong>Trang thiết bị hiện đại:</strong> Sở hữu dàn máy móc khoan cọc, ép cừ tiên tiến, đáp ứng mọi điều kiện địa chất.</li>
                <li><strong>An toàn tuyệt đối:</strong> Tuân thủ nghiêm ngặt các quy định về an toàn lao động và vệ sinh môi trường.</li>
                <li><strong>Giá cả cạnh tranh:</strong> Báo giá minh bạch, chi tiết, không phát sinh chi phí ngoài hợp đồng.</li>
              </ul>

              <p className="mt-8 text-center font-medium text-lg text-primary">
                Hãy liên hệ ngay với chúng tôi để được tư vấn và nhận báo giá tốt nhất cho công trình của bạn!
              </p>
            </>
          )}
          <ConstructionPhotos />
        </article>
      </div>
      
      <div className="w-full md:w-1/4 md:shrink-0">
        <Sidebar />
      </div>
    </div>
  );
}
